/**
 * A proposal the assistant drafted, as a card: each change listed, a
 * checkbox per change on a draft, and Approve / Reject (docs/chats.md §4).
 *
 * The card's state is read off the transcript, not kept here: items are
 * append-only, so a decision is a later `approval` item and the apply's
 * outcome another one (carrying `applied`), and a revision is a later
 * `proposal` item whose `supersedes_id` names this one. `proposalState` is
 * that fold, pure, so a reload draws exactly what a live session did.
 */

import React, { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'

import type { HgChatItem, HgProposalChange, HgProposalPreview, HgStructureChange } from '../../api/client'

type ProposalStatus = 'pending' | 'applying' | 'applied' | 'failed' | 'rejected' | 'replaced'

interface ProposalOutcome {
    status?: string
    applied?: number
    failed?: Array<{ key: string; reason: string }>
    error?: string
    /** The id each written key now has (structure changes). */
    ids?: Record<string, number>
}

export interface ProposalState {
    status: ProposalStatus
    /** The keys the user kept, when they approved part of a draft. */
    approvedSubset: string[] | null
    outcome: ProposalOutcome | null
}

/** What has happened to one proposal, from the items after it. */
export const proposalState = (proposalId: number, items: HgChatItem[]): ProposalState => {
    const state: ProposalState = { status: 'pending', approvedSubset: null, outcome: null }
    for (const item of items) {
        const content = item.content ?? {}
        if (item.item_type === 'proposal' && content.supersedes_id === proposalId) {
            if (state.status === 'pending') state.status = 'replaced'
        } else if (item.item_type === 'approval' && content.proposal_id === proposalId) {
            if (content.applied) {
                state.outcome = content.applied as ProposalOutcome
                state.status = content.applied.status === 'failed' ? 'failed' : 'applied'
            } else if (content.decision === 'rejected') {
                state.status = 'rejected'
            } else if (content.decision === 'approved') {
                state.status = 'applying'
                state.approvedSubset = Array.isArray(content.approved_subset)
                    ? content.approved_subset
                    : null
            }
        }
    }
    return state
}

const SHOWN_AT_FIRST = 8

const formatDay = (iso: string): string => {
    const [y, m, d] = iso.split('-').map(Number)
    if (!y || !m || !d) return iso
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    })
}

const OP_LABEL: Record<HgProposalChange['op'], string> = {
    create: 'New',
    update: 'Edit',
    delete: 'Delete',
}

const isStructure = (change: HgProposalChange | HgStructureChange): change is HgStructureChange =>
    'entity' in change

const ENTITY_WORDS: Record<HgStructureChange['entity'], [string, string]> = {
    project: ['project', 'projects'],
    feature: ['feature', 'features'],
    collection: ['collection', 'collections'],
    item: ['item', 'items'],
    summary: ['summary', 'summaries'],
    settings: ['settings', 'settings'],
}

const STRUCTURE_OP: Record<HgStructureChange['op'], [string, string]> = {
    create: ['New', 'new'],
    update: ['Edit', 'edited'],
    delete: ['Delete', 'deleted'],
}

/** "New project · 3 new features": one part per kind of change, in order. */
const structureHeadline = (changes: HgStructureChange[]): string => {
    const groups = new Map<string, { op: HgStructureChange['op']; entity: HgStructureChange['entity']; n: number }>()
    for (const c of changes) {
        const key = `${c.op}:${c.entity}`
        const group = groups.get(key) ?? { op: c.op, entity: c.entity, n: 0 }
        group.n += 1
        groups.set(key, group)
    }
    return [...groups.values()]
        .map(({ op, entity, n }) => {
            const [one, many] = ENTITY_WORDS[entity]
            return n === 1 ? `${STRUCTURE_OP[op][0]} ${one}` : `${n} ${STRUCTURE_OP[op][1]} ${many}`
        })
        .join(' · ')
}

const headline = (preview: HgProposalPreview): string => {
    const structure = (preview.items ?? []).filter(isStructure)
    if (structure.length) return structureHeadline(structure)
    const { create = 0, update = 0, delete: del = 0 } = preview.counts ?? {}
    const parts = []
    if (create) parts.push(`${create} new`)
    if (update) parts.push(`${update} edited`)
    if (del) parts.push(`${del} deleted`)
    return parts.join(' · ') || 'No changes'
}

/** What became of one change once the proposal was decided. */
type ChangeOutcome = 'saved' | 'skipped' | 'saving' | null

const OUTCOME_LABEL: Record<Exclude<ChangeOutcome, null>, string> = {
    saved: 'Saved',
    skipped: 'Skipped',
    saving: 'Saving',
}

const ChangeRow: React.FC<{
    change: HgProposalChange
    checkable: boolean
    checked: boolean
    dimmed: boolean
    failure: string | null
    outcome: ChangeOutcome
    onToggle: () => void
}> = ({ change, checkable, checked, dimmed, failure, outcome, onToggle }) => {
    const edit = change.change ?? {}
    const newBody = change.op === 'update' ? edit.body : undefined
    return (
        <li
            className={`flex gap-3 py-2 ${dimmed ? 'opacity-50' : ''}`}
            data-testid="proposal-item"
            data-key={change.key}
        >
            {checkable && (
                <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 accent-plum"
                    checked={checked}
                    onChange={onToggle}
                    aria-label={`Include ${formatDay(change.date)}`}
                    data-testid="proposal-item-checkbox"
                />
            )}
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>
                        {formatDay(change.date)}
                        {change.time ? `, ${change.time}` : ''}
                    </span>
                    <span className="rounded bg-gray-100 px-1.5 text-xs text-gray-600">
                        {OP_LABEL[change.op]}
                    </span>
                    {outcome && (
                        <span
                            className={`rounded px-1.5 text-xs ${
                                outcome === 'saved' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
                            }`}
                            data-testid="proposal-item-outcome"
                        >
                            {OUTCOME_LABEL[outcome]}
                        </span>
                    )}
                </div>
                {change.op === 'delete' ? (
                    <p className="whitespace-pre-wrap break-words text-gray-500 line-through">{change.body}</p>
                ) : newBody !== undefined ? (
                    <>
                        <p className="whitespace-pre-wrap break-words text-sm text-gray-400 line-through">
                            {change.body}
                        </p>
                        <p className="whitespace-pre-wrap break-words text-gray-800">{newBody}</p>
                    </>
                ) : (
                    <p className="whitespace-pre-wrap break-words text-gray-800">{change.body}</p>
                )}
                {edit.date && <p className="text-sm text-gray-600">Moves to {formatDay(edit.date)}</p>}
                {edit.time !== undefined && (
                    <p className="text-sm text-gray-600">{edit.time ? `Time: ${edit.time}` : 'No time'}</p>
                )}
                {(edit.tags_removed?.length || edit.tags_added?.length || edit.tags_kept?.length) ? (
                    <div className="mt-1 flex flex-wrap gap-1 text-xs">
                        {edit.tags_removed?.map((t) => (
                            <span key={`-${t}`} className="rounded bg-rose-50 px-1.5 py-0.5 text-rose-700 line-through">
                                {t}
                            </span>
                        ))}
                        {edit.tags_added?.map((t) => (
                            <span key={`+${t}`} className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">
                                {t}
                            </span>
                        ))}
                        {edit.tags_kept?.map((t) => (
                            <span key={`=${t}`} className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                                {t}
                            </span>
                        ))}
                    </div>
                ) : null}
                {change.tag_instruction && (
                    <p className="text-sm text-gray-600" data-testid="proposal-item-tag-instruction">
                        Tags: {change.tag_instruction}
                    </p>
                )}
                {change.future && (
                    <p className="text-sm text-amber-700" data-testid="proposal-item-future">
                        Dated after now.
                    </p>
                )}
                {change.repeats && (
                    <p className="text-sm text-amber-700" data-testid="proposal-item-repeats">
                        Already in another check-in that day.
                    </p>
                )}
                {failure && (
                    <p className="text-sm text-rose-600" data-testid="proposal-item-failed">
                        {failure === 'changed since preview' ? 'Changed since this draft. Not saved.' : 'Not saved.'}
                    </p>
                )}
            </div>
        </li>
    )
}

/** "2026-10-05" → "Oct 5, 2026"; anything else as it is. */
const formatLongDay = (iso: string): string => {
    const [y, m, d] = iso.split('-').map(Number)
    if (!y || !m || !d) return iso
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const shown = (value: unknown, kind?: string): string =>
    kind === 'date' && typeof value === 'string' && value ? formatLongDay(value) : String(value ?? '')

const OUTCOME_CLASS = (outcome: ChangeOutcome) =>
    outcome === 'saved' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'

/** Where a written project, feature, collection or item is on the site. */
const structureHref = (change: HgStructureChange, id: number | undefined): string | null => {
    if (change.entity === 'item') return change.collection_id ? `/collections/${change.collection_id}` : null
    if (id === undefined) return null
    if (change.entity === 'project') return `/projects/${id}`
    if (change.entity === 'feature') return `/features/${id}`
    if (change.entity === 'collection') return `/collections/${id}`
    return null
}

/** A settings change, one line per setting: "Time zone: America/New_York → Europe/Sofia". */
const SettingsRow: React.FC<{ change: HgStructureChange; dimmed: boolean; failure: string | null }> = ({
    change,
    dimmed,
    failure,
}) => (
    <li className={`py-2 ${dimmed ? 'opacity-50' : ''}`} data-testid="proposal-item" data-key={change.key}>
        {change.changes?.map((c) => (
            <p key={c.label} className="break-words text-gray-800" data-testid="proposal-setting">
                {c.label}: {c.from && <span className="text-gray-400">{c.from} → </span>}
                {c.to}
            </p>
        ))}
        {failure && (
            <p className="text-sm text-rose-600" data-testid="proposal-item-failed">
                {failure === 'changed since preview' ? 'Changed since this draft. Not saved.' : 'Not saved.'}
            </p>
        )}
    </li>
)

const StructureRow: React.FC<{
    change: HgStructureChange
    checkable: boolean
    checked: boolean
    dimmed: boolean
    failure: string | null
    outcome: ChangeOutcome
    writtenId: number | undefined
    onToggle: () => void
}> = ({ change, checkable, checked, dimmed, failure, outcome, writtenId, onToggle }) => {
    const deleted = change.op === 'delete'
    const href = outcome === 'saved' && !deleted ? structureHref(change, writtenId ?? change.id) : null
    const name = `${change.emoji ? `${change.emoji} ` : ''}${change.title}`
    return (
        <li
            className={`flex gap-3 py-2 ${dimmed ? 'opacity-50' : ''}`}
            data-testid="proposal-item"
            data-key={change.key}
        >
            {checkable && (
                <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 accent-plum"
                    checked={checked}
                    onChange={onToggle}
                    aria-label={`Include ${change.title}`}
                    data-testid="proposal-item-checkbox"
                />
            )}
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <span className="rounded bg-gray-100 px-1.5 text-xs text-gray-600">
                        {STRUCTURE_OP[change.op][0]} {ENTITY_WORDS[change.entity][0]}
                    </span>
                    {outcome && (
                        <span className={`rounded px-1.5 text-xs ${OUTCOME_CLASS(outcome)}`} data-testid="proposal-item-outcome">
                            {OUTCOME_LABEL[outcome]}
                        </span>
                    )}
                </div>
                {href ? (
                    <a href={href} className="break-words text-gray-800 underline" data-testid="proposal-item-link">
                        {name}
                    </a>
                ) : (
                    <p className={`break-words ${deleted ? 'text-gray-500 line-through' : 'text-gray-800'}`}>{name}</p>
                )}
                {change.fields?.map((f) => (
                    <p key={f.label} className="break-words text-sm text-gray-600">
                        {/* A date reads as a phrase: "Starts Oct 5, 2026". */}
                        {f.kind === 'date' ? `${f.label} ${shown(f.value, f.kind)}` : `${f.label}: ${shown(f.value)}`}
                    </p>
                ))}
                {change.changes?.map((c) => (
                    <p key={c.label} className="break-words text-sm text-gray-600">
                        {c.label}
                        {c.kind === 'date' ? ' ' : ': '}
                        {c.from && <span className="text-gray-400 line-through">{shown(c.from, c.kind)}</span>}{' '}
                        {c.to === null ? <span className="text-gray-500">Removed</span> : shown(c.to, c.kind)}
                    </p>
                ))}
                {change.consequences?.map((line) => (
                    <p key={line} className="text-sm text-rose-700" data-testid="proposal-item-consequence">
                        {line}
                    </p>
                ))}
                {change.repeats && (
                    <p className="text-sm text-amber-700" data-testid="proposal-item-repeats">
                        Already in {change.collection ?? 'this collection'}.
                    </p>
                )}
                {failure && (
                    <p className="text-sm text-rose-600" data-testid="proposal-item-failed">
                        {failure === 'changed since preview' ? 'Changed since this draft. Not saved.' : 'Not saved.'}
                    </p>
                )}
            </div>
        </li>
    )
}

const button = { type: 'spring', stiffness: 400, damping: 20 } as const

interface ProposalCardProps {
    item: HgChatItem
    state: ProposalState
    /** Resolves false when the decision did not go through. */
    onDecide: (proposalId: number, decision: 'approve' | 'reject', subset?: string[]) => Promise<boolean>
}

const ProposalCard: React.FC<ProposalCardProps> = ({ item, state, onDecide }) => {
    const proposalId = Number(item.content?.proposal_id ?? item.proposal_id)
    const preview = (item.content?.preview ?? { counts: {}, items: [] }) as HgProposalPreview
    const changes = preview.items ?? []
    const isDraft = item.content?.mode !== 'confirm'
    const [unchecked, setUnchecked] = useState<Set<string>>(() => new Set())
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [showAll, setShowAll] = useState(false)

    const pending = state.status === 'pending'
    const kept = changes.filter((c) => !unchecked.has(c.key))
    const failures = useMemo(
        () => new Map((state.outcome?.failed ?? []).map((f) => [f.key, f.reason])),
        [state.outcome],
    )
    const chosen = state.approvedSubset ? new Set(state.approvedSubset) : null

    if (state.status === 'replaced') {
        return (
            <div className="mb-4 text-sm text-gray-500" data-testid="proposal-card" data-status="replaced">
                Replaced by a newer draft.
            </div>
        )
    }

    const decide = async (decision: 'approve' | 'reject') => {
        setBusy(true)
        setError(null)
        const subset = decision === 'approve' && unchecked.size ? kept.map((c) => c.key) : undefined
        const ok = await onDecide(proposalId, decision, subset)
        setBusy(false)
        if (!ok) setError("Couldn't send that. Try again.")
    }

    const toggle = (key: string) =>
        setUnchecked((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })

    const visible = showAll ? changes : changes.slice(0, SHOWN_AT_FIRST)
    const saved = state.outcome?.applied ?? 0
    const decided = state.status === 'applying' || state.status === 'applied' || state.status === 'failed'
    const outcomeOf = (key: string): ChangeOutcome => {
        if (!decided) return null
        if (chosen !== null && !chosen.has(key)) return 'skipped'
        if (state.status === 'applying') return 'saving'
        if (state.status === 'applied' && !failures.has(key)) return 'saved'
        return null
    }
    const allSaved = saved === changes.length

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            style={{ transformOrigin: 'top center' }}
            className="mb-4 rounded-lg border border-gray-200 bg-white/90 p-3"
            data-testid="proposal-card"
            data-status={state.status}
        >
            <div className="text-sm font-medium text-gray-700">{headline(preview)}</div>
            {item.content?.persona_name && (
                <div className="text-xs text-gray-500" data-testid="proposal-by">
                    Suggested by {String(item.content.persona_name)}
                </div>
            )}
            <ul className="divide-y divide-gray-100">
                {visible.map((change) => {
                    const row = {
                        checked: !unchecked.has(change.key),
                        dimmed:
                            (pending && unchecked.has(change.key)) ||
                            (chosen !== null && !chosen.has(change.key)) ||
                            state.status === 'rejected',
                        failure: failures.get(change.key) ?? null,
                        outcome: outcomeOf(change.key),
                        onToggle: () => toggle(change.key),
                    }
                    const checkable = pending && isDraft && changes.length > 1
                    if (isStructure(change) && change.entity === 'settings') {
                        return <SettingsRow key={change.key} change={change} dimmed={row.dimmed} failure={row.failure} />
                    }
                    return isStructure(change) ? (
                        <StructureRow
                            key={change.key}
                            change={change}
                            checkable={checkable && !change.required}
                            writtenId={state.outcome?.ids?.[change.key]}
                            {...row}
                        />
                    ) : (
                        <ChangeRow key={change.key} change={change} checkable={checkable} {...row} />
                    )
                })}
            </ul>
            {preview.footnote && <p className="mt-1 text-sm text-gray-500">{preview.footnote}</p>}
            {changes.length > SHOWN_AT_FIRST && (
                <button
                    type="button"
                    className="text-sm text-gray-600 underline"
                    onClick={() => setShowAll((v) => !v)}
                >
                    {showAll ? 'Show fewer' : `Show all ${changes.length}`}
                </button>
            )}
            <div className="mt-2 flex items-center justify-end gap-2" data-testid="proposal-status">
                <AnimatePresence mode="wait" initial={false}>
                    {pending ? (
                        <motion.div
                            key="buttons"
                            className="flex gap-2"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.button
                                type="button"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                transition={button}
                                disabled={busy}
                                onClick={() => void decide('reject')}
                                className="rounded-md px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                data-testid="proposal-reject"
                            >
                                Reject
                            </motion.button>
                            <motion.button
                                type="button"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                transition={button}
                                disabled={busy || kept.length === 0}
                                onClick={() => void decide('approve')}
                                className="rounded-md bg-action hover:bg-action-dark px-3 py-1.5 text-sm font-medium text-plum disabled:opacity-50"
                                data-testid="proposal-approve"
                            >
                                Approve
                            </motion.button>
                        </motion.div>
                    ) : (
                        <motion.span
                            key={state.status}
                            className={`text-sm ${state.status === 'failed' ? 'text-rose-600' : 'text-gray-500'}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            {state.status === 'applying' && 'Saving…'}
                            {state.status === 'applied' && (allSaved ? 'Saved' : `Saved ${saved} of ${changes.length}`)}
                            {state.status === 'failed' && "Couldn't save"}
                            {state.status === 'rejected' && 'Rejected'}
                        </motion.span>
                    )}
                </AnimatePresence>
            </div>
            {error && <div className="mt-1 text-right text-sm text-rose-600">{error}</div>}
        </motion.div>
    )
}

export default ProposalCard
